import { slugify } from '@superbet-group/web.lib.utilities';
import { get as _get } from 'lodash';
import { Route } from 'vue-router';

import { sortBy } from '@core/utils/sort/sortBy';
import { getStringPropertyComparator } from '@core/utils/sort/stringPropertyComparator';
import { getFlagByCategory } from '@sportOfferFederation/flags/flagMapping';
import OfferEvent from '@core/models/offer/OfferEvent';
import { IState } from '@store/index';
import { getShellConfig } from '@src/config/shell/config';

export interface INode {
    id: number;
    name: string;
    urlName: string;
    children: INode[];
    eventCount: number;
    order: number | string;
    flag?: string;
    specialTournamentOrder?: number;
    tournamentName2?: string;
    tournamentName?: string;
    tournamentOrder?: number;
    icon?: string;
}

type StructElement = { name: string };
interface IStruct {
    sports: Record<number, StructElement>;
    categories: Record<number, StructElement>;
    tournaments: Record<number, StructElement>;
}

export type MatchForTree = {
    sportId: number;
    sportName?: string;
    sportOrder: number;
    categoryId: number;
    categoryName?: string;
    tournamentId: number | string;
    tournamentName?: string;
    specialTournamentOrder?: number;
    tournamentOrder?: number;
    tournamentName2?: string;
};

export const createTreeNode = (data: any, branch: Record<number, INode>, parentId?: number): INode => {
    const node: INode = { ...data };
    if (parentId) {
        node.flag = getFlagByCategory(node.id);
    }
    // eslint-disable-next-line no-param-reassign
    branch[node.id] = node;
    return node;
};

/**
 * @param data New data to put in node
 * @param branch Branch to put Node on
 * @param parentId Need this only for flags
 */
export const createOrUpdateTreeNode = (data: any, branch: Record<number, INode>, parentId?: number): INode => {
    const node: INode = branch[data.id] || createTreeNode(data, branch, parentId);
    node.eventCount += 1;
    return node;
};

export const buildTreeFromMatches = function (matches: MatchForTree[], struct?: IStruct): Record<number, INode> {
    const tree: Record<number, INode> = {};
    for (let i = 0; i < matches.length; i += 1) {
        const event = matches[i];
        const sport = createOrUpdateTreeNode(
            {
                id: event.sportId,
                name: event.sportName || _get(struct, ['sports', event.sportId, 'name']),
                urlName: _get(struct, ['sports', event.sportId, 'urlName']),
                children: {},
                eventCount: 0,
                order: event.sportOrder,
            },
            tree,
        );
        const category = createOrUpdateTreeNode(
            {
                id: event.categoryId,
                name: event.categoryName || _get(struct, ['categories', event.categoryId, 'name']),
                urlName: _get(struct, ['categories', event.categoryId, 'urlName']),
                children: {},
                eventCount: 0,
                order: event.categoryName,
            },
            sport.children,
            sport.id,
        );
        createOrUpdateTreeNode(
            {
                id: event.tournamentId,
                name: event.tournamentName || _get(struct, ['tournaments', event.tournamentId, 'name']),
                // virtual tournaments won't have urlName, but they are always in english for multilingual targets
                urlName: _get(struct, ['tournaments', event.tournamentId, 'urlName']) ?? event.tournamentName,
                children: [],
                eventCount: 0,
                specialTournamentOrder: event.specialTournamentOrder,
                tournamentOrder: event.tournamentOrder,
                tournamentName2: event.tournamentName2,
                tournamentName: event.tournamentName,
            },
            category.children,
            category.id,
        );
    }
    return tree;
};

export const orderTree = function (tree: INode['children']) {
    const orderedTree = sortBy(tree, 'order');

    for (let i = 0; i < orderedTree.length; i += 1) {
        const sport = orderedTree[i];
        sport.children = sortBy(sport.children, getStringPropertyComparator('order'));
        sport.children.forEach((category: any) => {
            // eslint-disable-next-line no-param-reassign
            category.children = sortBy<INode>(
                category.children,
                [
                    'specialTournamentOrder',
                    'tournamentOrder',
                    getStringPropertyComparator('tournamentName2'),
                    getStringPropertyComparator('tournamentName'),
                ],
            );
        });
    }
    return orderedTree;
};

export const pushToBreadcrumbIds = function (parent: INode[], slug: string, breadcrumbIds: INode['id'][], source: 'event' | 'route' = 'route') {
    if (!parent || !slug) {
        return null;
    }

    let nameKey = 'urlName';
    if (source === 'event') {
        nameKey = 'name';
    }

    const treeItem = parent.find((item: any) => slugify(item[nameKey]) === slug);
    if (treeItem) {
        breadcrumbIds.push(treeItem.id);
    }
    return treeItem;
};

const getSlugsFromSource = function (source: Route | OfferEvent) {
    if ('offerId' in source) {
        const sportSlug = slugify((source.sportName));
        const categorySlug = slugify(source.categoryName);
        const tournamentSlug = slugify(source.tournamentName);

        return { sportSlug, categorySlug, tournamentSlug };
    }

    const {
        sportSlug,
        categorySlug,
        tournamentSlug,
    } = source.params;

    return { sportSlug, categorySlug, tournamentSlug };
};

export const getBreadcrumbIds = function (source: Route | OfferEvent, tree: INode[], rootState: IState) {
    const breadcrumbIds: INode['id'][] = [];
    const {
        sportSlug,
        categorySlug,
        tournamentSlug,
    } = getSlugsFromSource(source);

    const sourceName = 'offerId' in source ? 'event' : 'route';
    const sportItem = pushToBreadcrumbIds(tree, sportSlug, breadcrumbIds, sourceName);
    /**
     * If sportItem is not found in the tree, it means that the sport does not have any events currently and is not part of the tree.
     * However, we don't need an event to map the slug to the id since sports are top level filters with unique slugs.
     * Ideally we wouldn't search for sport in the tree at all, but find it directly in full sports list from struct
     */
    if (!sportItem && getShellConfig().remoteConfig.sportBreadcrumbIdFromStruct) {
        const sport = Object.values(rootState.data.sportOffer.sports).find((item) => slugify(item.urlName) === sportSlug);
        if (sport) {
            breadcrumbIds.push(sport.id);
        }
    }
    const categoryItem = pushToBreadcrumbIds(_get(sportItem, 'children')!, categorySlug, breadcrumbIds, sourceName);

    pushToBreadcrumbIds(_get(categoryItem, 'children')!, tournamentSlug, breadcrumbIds, sourceName);
    return breadcrumbIds;
};
